import { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './PLInput.module.scss';

function PLInput({
  inputId,
  inputType = 'text',
  value,
  label,
  placeholder,
  maxLength = 100,
  inputIcon,
  prefixLabel,
  onInputChange,
  readOnly,
  currency, // "£", "₹", "$"
  min,
  // Browser autofill/autocomplete is off by default: these are campaign-config
  // fields (name, headline, limits…), not personal data, and the saved-value
  // dropdown caused the value to land in the DOM without reaching React state.
  autoComplete = 'off',
}) {
  // Only use POS-style if currency is defined
  const [rawCents, setRawCents] = useState(() => {
    if (!currency || value == null) return null;

    // Convert value to number whether it's a string or number
    const numericValue = typeof value === 'number' ? value : parseFloat(value);

    // Check if conversion succeeded
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(numericValue)) return null;

    return Math.round(numericValue * 100);
  });

  const [textValue, setTextValue] = useState(() => {
    if (!currency) return value || '';
    return '';
  });

  useEffect(() => {
    if (currency && typeof value === 'number') {
      setRawCents(Math.round(value * 100));
    } else if (!currency) {
      setTextValue(value || '');
    }
  }, [value, currency]);

  const formatCurrency = cents => {
    if (cents === null) return '';
    const number = cents / 100;
    return `${currency}${number.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const handleCurrencyKeyDown = e => {
    if (readOnly) return;

    if (e.key >= '0' && e.key <= '9') {
      e.preventDefault();
      const newCents = (rawCents || 0) * 10 + Number(e.key);
      setRawCents(newCents);
      onInputChange(newCents / 100);
    } else if (e.key === 'Backspace') {
      e.preventDefault();
      const newCents = rawCents ? Math.floor(rawCents / 10) : null;
      setRawCents(newCents);
      onInputChange(newCents ? newCents / 100 : 0);
    }
  };

  const handleTextChange = e => {
    const newValue = e.target.value;
    if (newValue.length > maxLength) return;
    // For number inputs with a floor (e.g. coupon limits), reject anything
    // below it so negatives can't be typed or pasted in.
    if (inputType === 'number' && newValue !== '' && min != null && Number(newValue) < min) {
      return;
    }
    setTextValue(newValue);
    onInputChange(newValue);
  };

  const displayValue = currency ? formatCurrency(rawCents) : textValue;

  // Match auth Input style exactly — icon floats on the right at 50%
  // opacity. Character counters, when needed, are rendered in the
  // consumer's label row via the CharCounter helper so every field
  // can show an icon (the auth pattern has no counters, just icons).
  const showIcon = !!inputIcon;
  const inputClass = `${styles.input}${showIcon ? ` ${styles.hasTrailing}` : ''}`;

  return (
    <div className={styles.customInputContainer}>
      {prefixLabel && ((currency && rawCents) || (!currency && textValue)) && (
        <span className={styles.prefixLabel}>{prefixLabel}</span>
      )}
      <input
        id={inputId}
        type={inputType}
        min={inputType === 'number' ? min : undefined}
        value={displayValue || ''}
        onChange={currency ? undefined : handleTextChange}
        onKeyDown={currency ? handleCurrencyKeyDown : undefined}
        placeholder={placeholder}
        readOnly={readOnly}
        className={inputClass}
        autoComplete={autoComplete}
        aria-label={label || placeholder || 'Input field'}
      />
      {showIcon && (
        // eslint-disable-next-line @next/next/no-img-element
        <img className={styles.inputIcon} src={inputIcon} alt="" aria-hidden="true" />
      )}
    </div>
  );
}

PLInput.propTypes = {
  inputId: PropTypes.string,
  inputType: PropTypes.oneOf(['text', 'password', 'email', 'number', 'tel', 'url']),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  maxLength: PropTypes.number,
  inputIcon: PropTypes.string,
  prefixLabel: PropTypes.string,
  onInputChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  currency: PropTypes.string,
  min: PropTypes.number,
  autoComplete: PropTypes.string,
};

export default memo(PLInput);
